/**
 * @file NumberSliderField.tsx
 * @description Число с ползунком — общее поле конструктора (phase19.16, партия
 * SB120 «свобода выбора», этап 0; site-builder.md SB120 «размер и отступы —
 * числом в разумных пределах»; проект
 * docs/phases/phase19.16-materials/uiux-sb120-fields.md §2).
 *
 * ```
 * Отступ сверху              ↺ как у колонки
 * ├───────●───┊────────────┤ [  24 ] px
 *  [0] [XS 8] [S 16] [M 32] [L 64]
 * ```
 *
 * Единицы пропсов: `value`, `min/max/step/largeStep`, `inherit.value`,
 * `presets`, `recommended`, `floor`, `marker` — ХРАНИМЫЕ (как в схеме);
 * `display.factor` переводит их в показанные (доля 0,4 × 100 = 40 %).
 *
 *  - `value === undefined` — наследуемое: бегунок полый, число серое, справа
 *    серым `inherit.label`; своё — справа «↺ inherit.label» (→ undefined);
 *  - запись (`onChange`) — по отпусканию, Enter, blur и ступени: один шаг
 *    отмены; во время перетаскивания и ↑/↓ в поле — `onPreview` раз в кадр;
 *  - Esc в поле возвращает прежнее; нечисло — «Введите число от A до B»;
 *    за пределом — поправка и «Наибольшее — B px»;
 *  - `recommended` — предупреждение вне диапазона (не запрет, SB120);
 *    `floor` — запрет по закону: min сдвигается, причина под полем (SB19).
 *
 * Правила и состояние — `useNumberSlider`, ступени — `NumberPresets`,
 * разбор ввода — `lib/site-fields/number-input`.
 */

import { RotateCcw } from "lucide-react";
import { useId } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { NoticeStrip } from "../ui/NoticeStrip";
import { FieldHint } from "../ui/settings-groups";
import { Slider } from "../ui/slider";
import { NumberPresets, type NumberPreset } from "./NumberPresets";
import { useNumberSlider, type NumberSliderInput } from "./useNumberSlider";
import { UNIT_META } from "../../lib/site-fields/number-input";
import { cn } from "@/lib/utils";

export interface NumberSliderFieldProps extends NumberSliderInput {
  label: string;
  /** Ступени-подсказки, не больше 5. */
  presets?: readonly NumberPreset[];
  /** Пояснение единицы под полем: «ширина ÷ высота». */
  hint?: string;
  disabled?: boolean;
  /** Курсор или фокус над полем — подсветка на канве (`space-highlight`). */
  onHover?: (inside: boolean) => void;
}

export function NumberSliderField(props: NumberSliderFieldProps) {
  const { label, value, inherit, unit, presets, floor, marker, hint, disabled, onHover } = props;
  const ids = { label: useId(), input: useId(), unit: useId(), warn: useId(), floor: useId(), note: useId() };
  const s = useNumberSlider(props);
  const short = UNIT_META[unit].short;
  const unitText = [`Значение ${UNIT_META[unit].describe}`, hint, marker?.label].filter(Boolean).join(". ");
  const describedBy = [ids.unit, s.warning && ids.warn, floor && ids.floor, s.note && ids.note].filter(Boolean).join(" ");

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-slot="number-slider-field"
      onMouseEnter={onHover ? () => onHover(true) : undefined}
      onMouseLeave={onHover ? () => onHover(false) : undefined}
      onFocus={onHover ? () => onHover(true) : undefined}
      onBlur={onHover ? () => onHover(false) : undefined}
    >
      <div className="flex min-h-8 items-center justify-between gap-2">
        <Label id={ids.label} htmlFor={ids.input}>
          {label}
        </Label>
        {s.inherited ? (
          <span className="type-caption text-on-surface-variant">{inherit.label}</span>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            // 48 на касании, 32 с мышью — как ступени (NumberPresets).
            className="h-12 can-hover:h-8"
            disabled={disabled}
            onClick={() => s.pick(undefined)}
            aria-label={`${label}: ${inherit.label}`}
          >
            <RotateCcw aria-hidden />
            {inherit.label}
          </Button>
        )}
      </div>
      {/* Ползунок тянется на всю ширину, поле 72 px — одинаково на 380 и 360. */}
      <div className="flex min-w-0 items-center gap-3">
        <Slider
          className="flex-1"
          value={s.shown}
          min={s.range.min}
          max={s.range.max}
          step={s.range.step}
          largeStep={s.large}
          disabled={disabled}
          inherited={s.hollow}
          marker={marker && s.markerShown !== undefined ? { value: s.markerShown, label: marker.label } : undefined}
          onValueChange={s.onSlide}
          onValueCommitted={s.onSlideEnd}
          thumbProps={{ "aria-labelledby": ids.label, "aria-describedby": describedBy, getAriaValueText: s.valueText }}
        />
        <div className="flex shrink-0 items-center gap-1">
          <Input
            id={ids.input}
            inputMode="decimal"
            autoComplete="off"
            disabled={disabled}
            value={s.text}
            placeholder={s.unknown ? "—" : undefined}
            aria-describedby={describedBy}
            onChange={(e) => s.onType(e.target.value)}
            onKeyDown={s.onInputKeyDown}
            onBlur={s.onInputBlur}
            className={cn("h-[var(--control-h,3rem)] w-18 text-right tabular-nums", s.inherited && !s.editing && "text-on-surface-variant")}
          />
          {short ? (
            <span aria-hidden className="type-caption min-w-5 text-on-surface-variant">
              {short}
            </span>
          ) : null}
        </div>
      </div>
      <span id={ids.unit} className="sr-only">
        {unitText}
      </span>
      {presets && presets.length > 0 ? (
        <NumberPresets presets={presets} value={value} inheritValue={inherit.value} inheritLabel={inherit.label} tolerance={props.step} label={`${label}: ступени`} disabled={disabled} onPick={s.pick} />
      ) : null}
      {hint ? <FieldHint className="mt-0">{hint}</FieldHint> : null}
      {floor ? (
        <FieldHint id={ids.floor} className="mt-0">
          {floor.reason}
        </FieldHint>
      ) : null}
      <div aria-live="polite">
        {s.note ? (
          <FieldHint id={ids.note} className="mt-0">
            {s.note}
          </FieldHint>
        ) : null}
      </div>
      {s.warning ? (
        <NoticeStrip tone="warning" className="mt-1">
          <span id={ids.warn}>{s.warning}</span>
        </NoticeStrip>
      ) : null}
    </div>
  );
}
